import React, { Component } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

class AdminCampaignsPage extends Component {
  state = {
    sources: [],
    campaigns: [],
    selectedSourceId: "",
    loading: false,

    platforms: [],
    showPlatformsPanel: false,
    showPlatformModal: false,
    platformFormId: "",
    platformFormName: "",
  };

  componentDidMount() {
    this.fetchSources();
    this.fetchPlatforms();
  }

  fetchSources = async () => {
    try {
      const res = await api.get("/admin/settings/sources");
      if (res.data.success) {
        this.setState({ sources: res.data.data });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching sources");
    }
  };

  fetchCampaigns = async (sourceId) => {
    if (!sourceId) {
      this.setState({ campaigns: [], selectedSourceId: "" });
      return;
    }
    this.setState({ loading: true, selectedSourceId: sourceId });
    try {
      const res = await api.get(`/admin/settings/campaigns?source_id=${sourceId}&fetch_all=1`);
      if (res.data.success) {
        this.setState({ campaigns: res.data.data });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching campaigns");
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchPlatforms = async () => {
    try {
      const res = await api.get("/admin/settings/platforms");
      if (res.data.success) {
        this.setState({ platforms: res.data.data });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching platforms");
    }
  };

  openPlatformModal = (platform = null) => {
    if (platform) {
      this.setState({ showPlatformModal: true, platformFormId: platform.id, platformFormName: platform.platform });
    } else {
      this.setState({ showPlatformModal: true, platformFormId: "", platformFormName: "" });
    }
  };

  closePlatformModal = () => this.setState({ showPlatformModal: false });

  savePlatform = async (e) => {
    e.preventDefault();
    const { platformFormId, platformFormName } = this.state;
    if (!platformFormName) return toast.error("Platform name is required");
    try {
      const res = await api.post("/admin/settings/platform", { id: platformFormId, platform: platformFormName });
      if (res.data.success) {
        toast.success(platformFormId ? "Platform updated" : "Platform added");
        this.closePlatformModal();
        this.fetchPlatforms();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving platform");
    }
  };

  togglePlatformStatus = async (platform) => {
    const newStatus = platform.status === 1 ? 0 : 1;
    try {
      const res = await api.post("/admin/settings/platform/status", { id: platform.id, active: newStatus });
      if (res.data.success) {
        toast.success(`Platform ${newStatus === 1 ? "Activated" : "Deactivated"} successfully`);
        this.fetchPlatforms();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating platform status");
    }
  };

  changeCampaignPlatform = async (campaign, platformId) => {
    try {
      const res = await api.post("/admin/settings/campaign/platform", {
        id: campaign.id,
        platform_id: platformId || null,
      });
      if (res.data.success) {
        toast.success("Campaign platform updated");
        this.fetchCampaigns(this.state.selectedSourceId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating campaign platform");
    }
  };

  toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 1 ? 0 : 1;
    try {
      const res = await api.post("/admin/settings/campaign/status", {
        id: campaign.id,
        active: newStatus,
      });
      if (res.data.success) {
        toast.success(`Campaign ${newStatus === 1 ? "Activated" : "Deactivated"} successfully`);
        // Refresh the data to apply the backend filter
        this.fetchCampaigns(this.state.selectedSourceId);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error updating campaign status");
    }
  };

  render() {
    const { sources, campaigns, selectedSourceId, loading, platforms, showPlatformsPanel } = this.state;
    const selectedSource = sources.find((s) => String(s.id) === String(selectedSourceId));
    const activePlatformCount = platforms.filter((p) => p.status === 1).length;

    return (
      <div className="adm-page">
        <div className="adm-page-inner">
          <div className="adm-header">
            <div>
              <h4 className="adm-header-title">
                <span className="adm-icon-badge"><i className="fas fa-bullhorn"></i></span>
                Campaign Operations
              </h4>
              <p className="adm-header-sub">Activate campaigns and map each one to a platform from a single control panel.</p>
            </div>
            <div className="adm-toolbar">
              <span className="adm-badge adm-badge-primary">
                <i className="fas fa-layer-group"></i>{activePlatformCount} platforms
              </span>
              <span className="adm-badge adm-badge-muted">
                <i className="fas fa-flag"></i>{campaigns.length} campaigns loaded
              </span>
              <button
                className="adm-btn adm-btn-outline adm-btn-sm"
                onClick={() => this.setState({ showPlatformsPanel: !showPlatformsPanel })}
              >
                <i className="fas fa-sliders-h"></i>
                {showPlatformsPanel ? "Hide Platforms" : "Manage Platforms"}
              </button>
            </div>
          </div>

          {showPlatformsPanel && (
            <div className="adm-card">
              <div className="adm-card-header">
                <h6><i className="fas fa-layer-group mr-2"></i>Campaign Platforms</h6>
                <button className="adm-btn adm-btn-primary adm-btn-sm" onClick={() => this.openPlatformModal()}>
                  <i className="fas fa-plus"></i>Add Platform
                </button>
              </div>
              <div className="adm-card-body">
                {platforms.length === 0 ? (
                  <div className="adm-empty">
                    <i className="fas fa-inbox"></i>
                    <strong>No platforms yet</strong>
                    <span>Add one to start mapping campaigns against it.</span>
                  </div>
                ) : (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Platform</th>
                          <th>Status</th>
                          <th className="num">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {platforms.map((p) => (
                          <tr key={p.id}>
                            <td style={{ color: "var(--adm-text-muted)" }}>#{p.id}</td>
                            <td style={{ fontWeight: 700 }}>{p.platform}</td>
                            <td>
                              <span className={`adm-badge ${p.status === 1 ? "adm-badge-success" : "adm-badge-muted"}`}>
                                {p.status === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="num">
                              <div className="d-flex justify-content-end" style={{ gap: "8px" }}>
                                <button className="adm-btn adm-btn-outline adm-btn-sm" onClick={() => this.openPlatformModal(p)}>
                                  <i className="fas fa-edit"></i>Edit
                                </button>
                                <button
                                  className={`adm-btn adm-btn-sm ${p.status === 1 ? "adm-btn-outline-danger" : "adm-btn-outline-success"}`}
                                  onClick={() => this.togglePlatformStatus(p)}
                                >
                                  <i className={`fas fa-${p.status === 1 ? "times" : "check"}`}></i>
                                  {p.status === 1 ? "Deactivate" : "Activate"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="adm-card">
            <div className="adm-card-body">
              <div className="adm-filter-bar" style={{ marginBottom: "20px" }}>
                <div className="adm-filter-group grow">
                  <label>Select Source</label>
                  <select value={selectedSourceId} onChange={(e) => this.fetchCampaigns(e.target.value)}>
                    <option value="">-- Select Source --</option>
                    {sources.map((s) => (
                      <option key={s.id} value={s.id}>{s.title}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-muted small" style={{ marginTop: "-12px", marginBottom: "18px" }}>
                {selectedSource ? `Showing campaigns for ${selectedSource.title}` : "Pick a source to load its campaigns."}
              </p>

              {loading ? (
                <div className="adm-spinner-row"><i className="fas fa-spinner fa-spin"></i></div>
              ) : selectedSourceId ? (
                campaigns.length > 0 ? (
                  <div className="adm-table-wrap">
                    <table className="adm-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Campaign Name</th>
                          <th>Platform</th>
                          <th>Status</th>
                          <th className="num">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaigns.map((c) => (
                          <tr key={c.id}>
                            <td style={{ color: "var(--adm-text-muted)" }}>#{c.id}</td>
                            <td style={{ fontWeight: 700 }}>{c.campaign_name}</td>
                            <td>
                              <select
                                style={{ minWidth: "170px", height: "34px", borderRadius: "8px", border: "1px solid var(--adm-border)", padding: "4px 10px", fontSize: "12.5px", background: "#f8fafc" }}
                                value={c.platform_id || ""}
                                onChange={(e) => this.changeCampaignPlatform(c, e.target.value)}
                              >
                                <option value="">-- No Platform --</option>
                                {platforms.map((p) => (
                                  <option key={p.id} value={p.id}>{p.platform}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <span className={`adm-badge ${c.status === 1 ? "adm-badge-success" : "adm-badge-muted"}`}>
                                {c.status === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="num">
                              <button
                                className={`adm-btn adm-btn-sm ${c.status === 1 ? "adm-btn-outline-danger" : "adm-btn-outline-success"}`}
                                style={{ minWidth: "112px" }}
                                onClick={() => this.toggleCampaignStatus(c)}
                              >
                                <i className={`fas fa-${c.status === 1 ? "times" : "check"}`}></i>
                                {c.status === 1 ? "Deactivate" : "Activate"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="adm-empty">
                    <i className="fas fa-folder-open"></i>
                    <strong>No campaigns found</strong>
                    <span>No campaigns are available for this source right now.</span>
                  </div>
                )
              ) : (
                <div className="adm-empty">
                  <i className="fas fa-hand-pointer"></i>
                  <strong>Select a source to continue</strong>
                  <span>Campaign data appears here once you choose a source above.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {this.state.showPlatformModal && (
          <div className="adm-modal-overlay">
            <div className="adm-modal-panel">
              <div className="adm-modal-header">
                <h5>
                  <i className={`fas fa-${this.state.platformFormId ? "edit" : "plus-circle"}`} style={{ color: "var(--adm-primary)" }}></i>
                  {this.state.platformFormId ? "Edit Platform" : "Add Platform"}
                </h5>
                <button type="button" className="adm-close-btn" onClick={this.closePlatformModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <form onSubmit={this.savePlatform}>
                <div className="adm-modal-body">
                  <div className="adm-filter-group">
                    <label>Platform Name</label>
                    <input
                      type="text" required
                      value={this.state.platformFormName}
                      onChange={(e) => this.setState({ platformFormName: e.target.value })}
                      placeholder="e.g. Google Ads"
                    />
                  </div>
                </div>
                <div className="adm-modal-footer">
                  <button type="button" className="adm-btn adm-btn-outline" onClick={this.closePlatformModal}>Cancel</button>
                  <button type="submit" className="adm-btn adm-btn-primary">
                    <i className="fas fa-save"></i>Save Platform
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default AdminCampaignsPage;
